import { supabase } from "@/integrations/supabase/client";
import { fetchAccountCapacity, formatCapacityError, isCapacityError } from "@/hooks/useAccountCapacity";

export interface CapacityBlock {
  accountName: string;
  clientId: string;
  capConnections: number;
  capMessages: number;
  allocatedConnections: number;
  allocatedMessages: number;
  requestedConnections: number;
  requestedMessages: number;
  maxAvailableConnections: number;
  maxAvailableMessages: number;
  message: string;
}

/**
 * Pre-flight capacity check before a campaign enters a state that consumes
 * daily capacity (draft/paused/active). Mirrors the DB trigger
 * `enforce_campaign_account_capacity`, but is stricter for humans: it blocks
 * whenever the account would be over its ceiling, even for a same-size
 * transition the trigger would grandfather through.
 */
export async function checkCampaignCapacity(
  campaignId: string
): Promise<{ ok: boolean; block?: CapacityBlock }> {

  const { data: campaign } = await supabase
    .from("campaigns")
    .select("id, client_id, max_connections_daily, max_messages_daily")
    .eq("id", campaignId)
    .maybeSingle();

  if (!campaign?.client_id) return { ok: true };

  const capacity = await fetchAccountCapacity(campaign.client_id, campaignId);
  if (!capacity) return { ok: true };

  const requestedConnections = campaign.max_connections_daily || 0;
  const requestedMessages = campaign.max_messages_daily || 0;

  const connFits = capacity.allocatedConnections + requestedConnections <= capacity.capConnections;
  const msgFits = capacity.allocatedMessages + requestedMessages <= capacity.capMessages;

  if (connFits && msgFits) return { ok: true };

  const parts: string[] = [];
  if (!connFits) {
    parts.push(
      `needs ${requestedConnections} connections/day but only ${capacity.availableConnections} are available`
    );
  }
  if (!msgFits) {
    parts.push(
      `needs ${requestedMessages} messages/day but only ${capacity.availableMessages} are available`
    );
  }

  return {
    ok: false,
    block: {
      accountName: capacity.accountName,
      clientId: capacity.clientId,
      capConnections: capacity.capConnections,
      capMessages: capacity.capMessages,
      allocatedConnections: capacity.allocatedConnections,
      allocatedMessages: capacity.allocatedMessages,
      requestedConnections,
      requestedMessages,
      maxAvailableConnections: capacity.availableConnections,
      maxAvailableMessages: capacity.availableMessages,
      message: `This campaign ${parts.join(" and ")} on ${capacity.accountName}. Free up capacity on its other campaigns first.`,
    },
  };
}


/**
 * Logs a user activity for a campaign
 * Includes the user's name for better traceability
 */
export async function logCampaignActivity(
  campaignId: string,
  action: string,
  details?: any
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    // Fetch user profile to get the name
    let userName = null;
    if (user?.id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('full_name')
        .eq('id', user.id)
        .single();
      userName = profile?.full_name || user.email || 'Unknown User';
    }

    await supabase
      .from('campaign_activity_logs' as any)
      .insert({
        campaign_id: campaignId,
        user_id: user?.id,
        action,
        details: {
          ...details,
          user_name: userName
        }
      });
  } catch (error) {
    console.error('Failed to log campaign activity:', error);
    // Non-blocking error - we don't want to stop the main action if logging fails
  }
}




/**
 * Normalizes variables in message templates to lowercase with underscores
 * Example: {First name} -> {first_name}, {COMPANY} -> {company}
 */
export const normalizeMessageVariables = (message: string): string => {
  if (!message) return message;

  // Replace all {Variable Name} patterns with {variable_name}
  return message.replace(/\{([^}]+)\}/g, (match, variable) => {
    // Convert to lowercase and replace spaces with underscores
    const normalized = variable.trim().toLowerCase().replace(/\s+/g, '_');
    return `{${normalized}}`;
  });
};

/**
 * Starts a campaign by generating initial jobs and updating status
 * Awaits the campaign-starter response to provide accurate error feedback
 */
export async function startCampaign(campaignId: string): Promise<{ success: boolean; jobsCreated: number; jobsEnqueued?: number; error?: string; reason?: 'capacity'; block?: CapacityBlock }> {
  try {
    // Get campaign details
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select('id, client_id, status')
      .eq('id', campaignId)
      .single();

    if (campaignError || !campaign) {
      throw new Error('Campaign not found');
    }

    if (!campaign.client_id) {
      throw new Error('Campaign does not have a client assigned');
    }

    // Capacity pre-flight before the campaign starts consuming daily actions
    const capacityCheck = await checkCampaignCapacity(campaignId);
    if (!capacityCheck.ok && capacityCheck.block) {
      return {
        success: false,
        jobsCreated: 0,
        reason: 'capacity',
        block: capacityCheck.block,
        error: capacityCheck.block.message,
      };
    }

    // Update campaign status to 'starting' for instant UI feedback
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({ status: 'starting' })
      .eq('id', campaignId);

    if (updateError) throw updateError;


    console.log(`✅ Campaign status set to 'starting', invoking campaign-starter...`);

    // AWAIT the campaign-starter response for accurate error handling
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('campaign-starter', {
        body: {
          campaign_id: campaignId,
          client_id: campaign.client_id,
          triggered_by: 'user_start_campaign'
        }
      });

      if (invokeError) {
        console.error('Campaign starter invoke error:', invokeError);
        // Revert status on error
        await supabase
          .from('campaigns')
          .update({ status: 'draft' })
          .eq('id', campaignId);
        throw new Error(invokeError.message || 'Failed to start campaign');
      }

      if (!data?.success) {
        console.error('Campaign starter returned error:', data?.error);
        // Revert status on error
        await supabase
          .from('campaigns')
          .update({ status: 'draft' })
          .eq('id', campaignId);
        throw new Error(data?.error || data?.message || 'Campaign start failed');
      }

      console.log(`✅ Campaign started: ${data.jobs_created} jobs created`);

      // Update to active on success
      await supabase
        .from('campaigns')
        .update({ status: 'active' })
        .eq('id', campaignId);

      await logCampaignActivity(campaignId, 'started', { jobs_created: data.jobs_created });

      return {
        success: true,
        jobsCreated: data.jobs_created ?? 0,
        jobsEnqueued: 0,
      };
    } catch (invokeErr: any) {
      console.error('Campaign starter failed:', invokeErr);
      // Revert status on error
      await supabase
        .from('campaigns')
        .update({ status: 'draft' })
        .eq('id', campaignId);
      throw invokeErr;
    }

  } catch (error: any) {
    console.error('Error starting campaign:', error);
    return {
      success: false,
      jobsCreated: 0,
      error: error.message,
    };
  }
}

/**
 * Pauses a campaign by updating status and canceling pending jobs
 */
export async function pauseCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Update campaign status first for immediate UI feedback
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({ status: 'paused' })
      .eq('id', campaignId);

    if (updateError) throw updateError;

    // Cancel all pending, scheduled, AND active jobs directly (no edge function needed)
    const { error: jobsError } = await supabase
      .from('job_queue')
      .update({
        status: 'cancelled',
        error_message: 'Campaign paused by user',
        failed_at: new Date().toISOString()
      })
      .eq('campaign_id', campaignId)
      .in('status', ['pending', 'scheduled', 'active']);

    if (jobsError) throw jobsError;

    await logCampaignActivity(campaignId, 'paused', { reason: 'user_action' });
    console.log(`✅ Campaign paused, jobs cancelled`);
    return { success: true };

  } catch (error: any) {
    console.error('Error pausing campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Archives a campaign by updating status and canceling pending jobs.
 * Archived campaigns can be unarchived later.
 */
export async function archiveCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Update campaign status to 'archived'
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({ status: 'archived' })
      .eq('id', campaignId);

    if (updateError) throw updateError;

    // Cancel all pending, scheduled, AND active jobs directly
    const { error: jobsError } = await supabase
      .from('job_queue')
      .update({
        status: 'cancelled',
        error_message: 'Campaign archived by user',
        failed_at: new Date().toISOString()
      })
      .eq('campaign_id', campaignId)
      .in('status', ['pending', 'scheduled', 'active']);

    if (jobsError) throw jobsError;

    await logCampaignActivity(campaignId, 'archived');
    console.log(`✅ Campaign archived, jobs cancelled`);
    return { success: true };

  } catch (error: any) {
    console.error('Error archiving campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Unarchives a campaign by changing status from 'archived' to 'paused'.
 * Checks if the associated client is archived and tries to find a replacement active client with the same email.
 * BLOCKS unarchiving if account_deleted is true (LinkedIn account was permanently deleted).
 */
export async function unarchiveCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Get campaign and current client info, including account_deleted flag
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select('id, client_id, account_deleted, clients (id, status, email, workspace_id)')
      .eq('id', campaignId)
      .single();

    if (campaignError || !campaign) throw new Error('Campaign not found');

    // 2. Block unarchiving if the LinkedIn account was permanently deleted
    if ((campaign as any).account_deleted) {
      return {
        success: false,
        error: 'This campaign cannot be restored because the LinkedIn account was permanently deleted.'
      };
    }

    const client = campaign.clients as any;

    // 3. Check if client is archived (soft-deleted)
    if (client && client.status === 'archived') {
      // Search for a new active client with same email in same workspace
      const { data: newClient } = await supabase
        .from('clients')
        .select('id')
        .eq('workspace_id', client.workspace_id)
        .eq('email', client.email)
        .neq('status', 'archived')
        .neq('status', 'disconnected')
        .limit(1)
        .maybeSingle();

      if (newClient) {
        // Found a matching active client! Relink the campaign.
        const { error: relinkError } = await supabase
          .from('campaigns')
          .update({ client_id: newClient.id })
          .eq('id', campaignId);

        if (relinkError) throw relinkError;
      } else {
        // No matching active client found
        return {
          success: false,
          error: `Cannot restore campaign. Please reconnect the LinkedIn account (${client.email}) first to enable restoration.`
        };
      }
    }

    // 4. Capacity pre-flight — 'paused' consumes account capacity
    const capacityCheck = await checkCampaignCapacity(campaignId);
    if (!capacityCheck.ok && capacityCheck.block) {
      return { success: false, error: capacityCheck.block.message };
    }

    // 5. Proceed with unarchiving
    const { error } = await supabase
      .from('campaigns')
      .update({ status: 'paused' })
      .eq('id', campaignId)
      .eq('status', 'archived');

    if (error) {
      if (isCapacityError(error)) {
        return { success: false, error: formatCapacityError(error) };
      }
      throw error;
    }


    await logCampaignActivity(campaignId, 'unarchived');
    return { success: true };
  } catch (error: any) {
    console.error('Error unarchiving campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Soft deletes a campaign (marks as deleted)
 */
export async function deleteCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // Set deleted_at timestamp instead of hard delete
    const { error: campaignError } = await supabase
      .from('campaigns')
      .update({ deleted_at: new Date().toISOString(), deleted_by: (await supabase.auth.getUser()).data.user?.id ?? null } as any)
      .eq('id', campaignId);

    if (campaignError) throw campaignError;

    await logCampaignActivity(campaignId, 'deleted');
    return { success: true };

  } catch (error: any) {
    console.error('Error deleting campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Orphans a campaign instead of permanently deleting it.
 * The campaign is unlinked from its workspace and client but all data 
 * (targets, sequences, stats, logs) is preserved. Super admins can 
 * recover orphaned campaigns from the admin panel.
 */
export async function permanentlyDeleteCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    // 1. Fetch the campaign's current workspace name before orphaning
    const { data: campaign, error: fetchError } = await supabase
      .from('campaigns')
      .select('workspace_id, client_id, status')
      .eq('id', campaignId)
      .single();

    if (fetchError || !campaign) throw new Error('Campaign not found');

    let workspaceName: string | null = null;
    if (campaign.workspace_id) {
      const { data: ws } = await supabase
        .from('workspaces')
        .select('name')
        .eq('id', campaign.workspace_id)
        .single();
      workspaceName = ws?.name || null;
    }

    // 2. Orphan the campaign: unlink from workspace/client, record metadata
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({
        // Stays in its workspace, hidden from customers; super admins still see it.
        admin_only: true,
        deleted_at: new Date().toISOString(),
        previous_workspace_name: workspaceName,
        status: 'archived',
      } as any)
      .eq('id', campaignId);

    if (updateError) throw updateError;

    // 3. Cancel any remaining active/pending jobs
    await supabase
      .from('job_queue')
      .update({
        status: 'cancelled',
        error_message: 'Campaign orphaned',
        failed_at: new Date().toISOString()
      })
      .eq('campaign_id', campaignId)
      .in('status', ['pending', 'scheduled', 'active']);

    return { success: true };

  } catch (error: any) {
    console.error('Error orphaning campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Restores a soft-deleted campaign
 */
export async function restoreCampaign(campaignId: string): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('campaigns')
      .update({ deleted_at: null })
      .eq('id', campaignId);

    if (error) throw error;

    await logCampaignActivity(campaignId, 'restored');
    return { success: true };
  } catch (error: any) {
    console.error('Error restoring campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}

/**
 * Resumes a paused campaign using atomic backend job creation
 * Awaits the job-orchestrator response to provide accurate error feedback
 */
export async function resumeCampaign(campaignId: string): Promise<{ success: boolean; jobsCreated?: number; error?: string; reason?: 'capacity'; block?: CapacityBlock }> {
  try {
    // Check current status first to prevent resuming archived campaigns
    const { data: campaign, error: fetchError } = await supabase
      .from('campaigns')
      .select('status, client_id')
      .eq('id', campaignId)
      .single();

    if (fetchError) throw fetchError;
    if (campaign?.status === 'archived') {
      throw new Error("Cannot resume an archived campaign. Please create a new campaign instead.");
    }
    if (!campaign?.client_id) {
      throw new Error("Campaign has no linked LinkedIn account.");
    }

    // Capacity pre-flight: never leave a campaign stranded in 'resuming'
    // because the account ceiling would be breached.
    const capacityCheck = await checkCampaignCapacity(campaignId);
    if (!capacityCheck.ok && capacityCheck.block) {
      return {
        success: false,
        jobsCreated: 0,
        reason: 'capacity',
        block: capacityCheck.block,
        error: capacityCheck.block.message,
      };
    }


    // Update campaign status to 'resuming' for instant UI feedback
    const { error: updateError } = await supabase
      .from('campaigns')
      .update({ status: 'resuming' })
      .eq('id', campaignId);

    if (updateError) throw updateError;


    // Clear pause leftovers before rescheduling. Manual pauses stamp
    // "Campaign paused by user", auto-pauses (disconnect, cap, expiry) stamp
    // their own reasons — all of them must go, otherwise the rescheduler sees
    // ghost rows and under-books the resumed campaign.
    await supabase
      .from('job_queue')
      .delete()
      .eq('campaign_id', campaignId)
      .eq('status', 'cancelled')
      .or(
        'error_message.ilike.%pause%,error_message.ilike.%disconnect%,error_message.ilike.%campaign stopped%,error_message.is.null'
      );


    console.log('✅ Campaign status set to resuming, invoking job-orchestrator...');

    await logCampaignActivity(campaignId, 'resumed', { jobs_created: -1 });

    // AWAIT the job-orchestrator response for accurate error handling
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('job-orchestrator', {
        body: {
          trigger: 'campaign_resume',
          campaignId: campaignId
        }
      });

      // NOTE: the server (job-orchestrator) now settles the final status too,
      // so every client write below is guarded to only apply while the
      // campaign is still transient — it must never override the server's
      // settlement or a newer user action.
      if (invokeError) {
        console.error('job-orchestrator invoke error:', invokeError);
        await supabase
          .from('campaigns')
          .update({ status: 'paused' })
          .eq('id', campaignId)
          .in('status', ['resuming', 'starting']);
        throw new Error(invokeError.message || 'Failed to schedule resume jobs');
      }

      if (!data?.success) {
        console.error('job-orchestrator returned error:', data?.error);
        await supabase
          .from('campaigns')
          .update({ status: 'paused' })
          .eq('id', campaignId)
          .in('status', ['resuming', 'starting']);
        throw new Error(data?.error || data?.message || 'Job scheduling failed');
      }

      console.log(`✅ Resume jobs created: ${data.jobsCreated} jobs`);

      // Update to active on success
      const { error: activateError } = await supabase
        .from('campaigns')
        .update({ status: 'active' })
        .eq('id', campaignId)
        .in('status', ['resuming', 'starting']);


      // Race guard: another campaign may have claimed capacity meanwhile.
      if (activateError) {
        await supabase
          .from('campaigns')
          .update({ status: 'paused' })
          .eq('id', campaignId)
          .in('status', ['resuming', 'starting']);

        if (isCapacityError(activateError)) {
          return {
            success: false,
            jobsCreated: 0,
            reason: 'capacity',
            error: formatCapacityError(activateError),
          };
        }
        throw activateError;
      }

      return {
        success: true,
        jobsCreated: data.jobsCreated ?? 0,
      };

    } catch (invokeErr: any) {
      console.error('job-orchestrator failed:', invokeErr);
      // Revert status on error — only if the server hasn't already settled it.
      await supabase
        .from('campaigns')
        .update({ status: 'paused' })
        .eq('id', campaignId)
        .in('status', ['resuming', 'starting']);
      throw invokeErr;
    }

  } catch (error: any) {
    console.error('Error resuming campaign:', error);
    return {
      success: false,
      jobsCreated: 0,
      error: error.message,
    };
  }
}

/**
 * Duplicates a campaign with all its sequences AND targets (including custom variables)
 */
/**
 * Duplicates a campaign with all its sequences AND optionally targets
 * @param options.includeTargets - Whether to include target leads
 * @param options.excludeMessaged - Whether to exclude targets that have already received messages (only applies if includeTargets is true)
 */
export async function duplicateCampaign(
  campaignId: string,
  options: { includeTargets: boolean; excludeMessaged?: boolean } = { includeTargets: true, excludeMessaged: false }
): Promise<{ success: boolean; newCampaignId?: string; error?: string }> {
  try {
    // Get original campaign
    const { data: campaign, error: campaignError } = await supabase
      .from('campaigns')
      .select('*')
      .eq('id', campaignId)
      .single();

    if (campaignError || !campaign) {
      throw new Error('Campaign not found');
    }

    // Get original sequences
    const { data: sequences, error: seqError } = await supabase
      .from('sequences')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('step_number', { ascending: true });

    if (seqError) throw seqError;

    // Get original targets ONLY if requested
    let targets: any[] = [];
    if (options.includeTargets) {
      const { data: t, error: targetsError } = await supabase
        .from('targets')
        .select('*')
        .eq('campaign_id', campaignId);

      if (targetsError) throw targetsError;
      targets = t || [];
    }

    // Clamp the copy's daily limits to whatever the account still has free,
    // so duplication can never push the account over its ceiling.
    let copyConnections = campaign.max_connections_daily || 0;
    let copyMessages = campaign.max_messages_daily || 0;
    if (campaign.client_id) {
      const capacity = await fetchAccountCapacity(campaign.client_id);
      if (capacity) {
        copyConnections = Math.min(copyConnections, capacity.availableConnections);
        copyMessages = Math.min(copyMessages, capacity.availableMessages);
      }
    }

    // Create new campaign (copy all settings except id, timestamps, status)
    const { data: newCampaign, error: createError } = await supabase
      .from('campaigns')
      .insert({
        name: `${campaign.name} (Copy)`,
        client_id: campaign.client_id,
        user_id: campaign.user_id,
        status: 'draft',
        timezone: campaign.timezone,
        working_hours_start: campaign.working_hours_start,
        working_hours_end: campaign.working_hours_end,
        working_days: campaign.working_days,
        max_connections_daily: copyConnections,
        max_messages_daily: copyMessages,
        respect_working_hours: campaign.respect_working_hours,
        connection_timeout_days: campaign.connection_timeout_days,
      })
      .select()
      .single();


    if (createError || !newCampaign) {
      throw new Error('Failed to create campaign copy');
    }

    // Duplicate sequences if any exist
    if (sequences && sequences.length > 0) {
      // Map to store old_sequence_id -> new_sequence_id for parent referencing
      const sequenceIdMap = new Map<string, string>();

      // Sort sequences by step_number to ensure parents are likely created before children
      // If step numbers are same, rely on parent_step_id being null for roots
      const sortedSequences = [...sequences].sort((a, b) => {
        if (a.step_number !== b.step_number) return a.step_number - b.step_number;
        // If same step (branches), put the one without parent first (though usually branches have parents)
        return 0;
      });

      // Insert sequences one by one to maintain ID mapping for tree structure
      for (const seq of sortedSequences) {
        // Resolve parent_step_id using the map
        const newParentId = seq.parent_step_id ? sequenceIdMap.get(seq.parent_step_id) : null;

        // If we have a parent ID but couldn't resolve it, this might be an ordering issue
        // or the parent wasn't copied. For now, we proceed (it might detach the branch).
        // In a strictly valid tree, parents should always be processed first if we sort correctly.

        const { data: newSeq, error: seqInsertError } = await supabase
          .from('sequences')
          .insert({
            campaign_id: newCampaign.id,
            step_number: seq.step_number,
            action_type: seq.action_type,
            message_template: seq.message_template,
            delay_hours: seq.delay_hours,
            include_message: seq.include_message,
            use_inmail: seq.use_inmail,
            attachment_urls: seq.attachment_urls,
            condition_type: seq.condition_type,
            condition_value: seq.condition_value,
            branch_type: seq.branch_type,
            final_wait_hours: seq.final_wait_hours,
            fallback_template: seq.fallback_template ?? null,
            fallback_enabled: seq.fallback_enabled === true,
            parent_step_id: newParentId, // Use the mapped new ID
          })
          .select('id')
          .single();

        if (seqInsertError) {
          // Rollback: delete the created campaign and its sequences
          await supabase.from('sequences').delete().eq('campaign_id', newCampaign.id);
          await supabase.from('campaigns').delete().eq('id', newCampaign.id);
          throw new Error('Failed to duplicate sequence step: ' + seqInsertError.message);
        }

        if (newSeq) {
          sequenceIdMap.set(seq.id, newSeq.id);
        }
      }
    }

    // Duplicate targets if requested and exist
    if (options.includeTargets && targets && targets.length > 0) {
      // Filter out targets that have received messages if excludeMessaged is true
      let filteredTargets = targets;
      if (options.excludeMessaged) {
        filteredTargets = targets.filter(target => {
          // Exclude targets where any message step was completed
          // Check if first_message_sent_at is set OR current_sequence_step > 1 (meaning at least connection + message)
          const hasReceivedMessage = target.first_message_sent_at != null || target.current_sequence_step > 1;
          return !hasReceivedMessage;
        });
      }

      if (filteredTargets.length === 0) {
        // No targets to copy after filtering
        return { success: true, newCampaignId: newCampaign.id };
      }

      // Process in batches of 500 to avoid payload size limits
      const BATCH_SIZE = 500;
      for (let i = 0; i < filteredTargets.length; i += BATCH_SIZE) {
        const batch = filteredTargets.slice(i, i + BATCH_SIZE);
        const newTargets = batch.map(target => ({
          campaign_id: newCampaign.id,
          profile_url: target.profile_url,
          first_name: target.first_name,
          last_name: target.last_name,
          name: target.name,
          title: target.title,
          company: target.company,
          custom_data: target.custom_data,
          sales_nav_url: target.sales_nav_url,
          link_type: target.link_type,
          linkedin_profile_id: target.linkedin_profile_id,
          provider_id: target.provider_id, // Ensure provider_id is copied
          // Always reset progress for fresh start
          status: 'pending',
          current_sequence_step: 0,
        }));

        const { error: targetsInsertError } = await supabase
          .from('targets')
          .insert(newTargets);

        if (targetsInsertError) {
          // Rollback: delete sequences, targets and campaign
          await supabase.from('sequences').delete().eq('campaign_id', newCampaign.id);
          await supabase.from('targets').delete().eq('campaign_id', newCampaign.id);
          await supabase.from('campaigns').delete().eq('id', newCampaign.id);
          throw new Error('Failed to duplicate targets: ' + targetsInsertError.message);
        }
      }
    }

    return { success: true, newCampaignId: newCampaign.id };

  } catch (error: any) {
    console.error('Error duplicating campaign:', error);
    return {
      success: false,
      error: error.message,
    };
  }
}
